import { useRef, useState, useSyncExternalStore } from "react";
import {
  thisBrowsersStorage,
  writeStoredSettings,
} from "../src/settings-bridge.js";
import { storedSettings, subscribeToSettings } from "./settings-mirror.js";
import { SETTINGS_LIVE_HERE, SETUP_IN_THE_LINK } from "./settings-copy.js";
import {
  readSettingsFile,
  settingsFileName,
  settingsFileText,
} from "./settings-file.js";
import { download } from "./save.js";
import {
  applySetup,
  setupFromSettings,
  setupUrlFor,
  thisPagesSetup,
  withoutSetupParams,
} from "./settings-url.js";

/**
 * Getting a setup out of this browser and back into one (web ticket 18, stories
 * 59 to 61).
 *
 * The section that exists because there are no accounts and no server-side
 * per-user state, which is a requirement of the spec rather than a default. That
 * makes settings per browser profile, and this is both of the mitigations that
 * need no server plus the sentence saying why they are here.
 *
 * **Everything on it works off the page's own copy of the blob**, not a request
 * to the worker. The page is what persists settings — it reads the blob at
 * startup and stores every write the worker posts back — so it already holds the
 * thing to export, and export needs no new channel on the contract for that
 * reason alone.
 */
export function SettingsTransfer({
  disabled = false,
}: {
  /** True while a take is in progress. Import replaces everything the
   * application was constructed from and reloads to apply it, which is the same
   * reason refresh is refused mid-take (FR-17) with a worse outcome. */
  disabled?: boolean;
}): JSX.Element {
  const setup = thisPagesSetup();
  /**
   * What is in force: what this browser has stored, with the address over the
   * top. What you see is what gets exported.
   *
   * Subscribed to rather than read once. Each row on this screen holds its own
   * state, so changing the scroll rate re-renders one row and nothing above it —
   * and a file exported after that would be the setup from before the change,
   * which is a quiet way to fail "import restores them exactly".
   */
  const stored = useSyncExternalStore(subscribeToSettings, storedSettings);
  const inForce = applySetup(stored, setup);

  const [problem, setProblem] = useState<string>();
  const [exported, setExported] = useState(false);
  const chooser = useRef<HTMLInputElement>(null);

  return (
    <section className="settings-screen web-transfer">
      <ul className="web-transfer-note">
        {SETTINGS_LIVE_HERE.map((line) => (
          <li key={line.what}>
            <strong>{line.what}</strong> <span>{line.detail}</span>
          </li>
        ))}
      </ul>

      <div className="settings-actions">
        <button
          type="button"
          onClick={() => {
            setProblem(undefined);
            download(
              settingsFileName(),
              new Blob([settingsFileText(inForce)], {
                type: "application/json",
              }),
            );
            setExported(true);
          }}
          title="Save every setting to a file you can keep or copy to another machine"
        >
          Export settings to a file
        </button>
        {/* The file chooser is the input; the button is what anyone will look
          * for. A bare file input on a settings screen reads as a form to fill
          * in rather than as an action to take. */}
        <button
          type="button"
          onClick={() => {
            setProblem(undefined);
            chooser.current?.click();
          }}
          disabled={disabled}
          title={
            disabled
              ? "Not while a take is recording — importing settings reloads the page"
              : "Replace every setting with the ones in a file cuekards exported"
          }
        >
          Import settings from a file
        </button>
        {exported && (
          <span className="settings-ack" role="status">
            ✓ Exported
          </span>
        )}
      </div>

      <input
        ref={chooser}
        className="web-transfer-file"
        type="file"
        accept="application/json,.json"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared straight away so that choosing the same file twice — after
          // fixing whatever was wrong with it — still fires a change.
          event.target.value = "";
          if (file !== undefined) void importFrom(file, setProblem);
        }}
      />

      <p className="settings-note">{SETUP_IN_THE_LINK}</p>
      <SetupLink href={setupUrlFor(window.location.href, setupFromSettings(inForce))} />

      {problem !== undefined && (
        <p className="web-error" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}

/**
 * The address that carries this rig, to copy.
 *
 * Read-only and selectable rather than a link to click: this is a thing to
 * bookmark or paste into a message on another machine, and following it from
 * here would do nothing visible — you are already on it, give or take the
 * parameters it is offering to add.
 */
function SetupLink({ href }: { href: string }): JSX.Element {
  const box = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  return (
    <div className="web-transfer-link">
      <input
        ref={box}
        className="web-setup-url"
        type="text"
        readOnly
        value={href}
        spellCheck={false}
        onFocus={(event) => event.target.select()}
      />
      <button
        type="button"
        onClick={() => {
          // Selected as well as copied, so that a browser refusing the
          // clipboard leaves the address ready for Ctrl+C rather than leaving
          // the button apparently dead.
          box.current?.select();
          void navigator.clipboard?.writeText(href).catch(() => undefined);
          setCopied(true);
        }}
        title="Copy this address"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

/**
 * Take a chosen file as the settings, and reload onto them.
 *
 * **The reload is the mechanism and not a shortcut.** Settings are read by every
 * controller as it is constructed — which script source, which devices, which
 * keys, whether the recogniser is listening for commands — so replacing the blob
 * under a running application would leave the worker holding one setup and the
 * screen showing another. A page load is the path that applies a whole settings
 * blob correctly, it is exercised on every single visit, and it costs a second.
 *
 * It reloads onto an address with no setup on it, because the file just chosen
 * is the setup now: coming back to a URL that still overrode it would be an
 * import that appeared to have done nothing.
 */
async function importFrom(
  file: File,
  report: (problem: string) => void,
): Promise<void> {
  let text: string;
  try {
    text = await file.text();
  } catch {
    report(`Could not read ${file.name}. Try exporting it again.`);
    return;
  }

  const read = readSettingsFile(text);
  if (!read.ok) {
    report(read.message);
    return;
  }

  const storage = thisBrowsersStorage();
  if (storage === undefined) {
    report(
      "This browser is not storing site data, so there is nowhere to import " +
        "these settings to. Allow site data for cuekards and try again.",
    );
    return;
  }
  writeStoredSettings(storage, read.settings);

  const plain = withoutSetupParams(window.location.href);
  // `reload` rather than `replace` where there is nothing to strip: a replace to
  // the same address is a fresh *navigation*, and a `?doc=` in the address means
  // something different on one of those — it launches the prompter (see
  // `isFreshArrival`). Importing settings is not arriving from a shared link.
  if (plain === window.location.href) window.location.reload();
  else window.location.replace(plain);
}
